import React from 'react';
import { Shield, Lock, Zap, CheckCircle, Eye, FileCheck, TrendingUp, Award, ArrowRight, AlertCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const Soc2Services = () => {
  const navigate = useNavigate();

  const trustServicesCriteria = [
    {
      icon: Shield,
      title: 'Security',
      description: 'Safeguarding information and systems against unauthorized access.',
      isMandatory: true,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Zap,
      title: 'Availability',
      description: 'Ensuring systems are operational and accessible for use as agreed.',
      isMandatory: false,
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: CheckCircle,
      title: 'Processing Integrity',
      description: 'Confirming that system processing is complete, valid, accurate, and timely.',
      isMandatory: false,
      gradient: 'from-purple-500 to-indigo-500'
    },
    {
      icon: Lock,
      title: 'Confidentiality',
      description: 'Protecting information designated as confidential.',
      isMandatory: false,
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Eye,
      title: 'Privacy',
      description: 'Managing personal information in accordance with an organization\'s privacy notice.',
      isMandatory: false,
      gradient: 'from-pink-500 to-rose-500'
    }
  ];

  const reportTypes = [
    {
      type: 'Type I',
      focus: 'A "snapshot" of controls at a specific point in time.',
      bestFor: 'Companies needing a report quickly to satisfy a prospect.',
      icon: Clock,
      color: 'blue'
    },
    {
      type: 'Type II',
      focus: 'Evaluates the operating effectiveness of controls over a period (usually 6–12 months).',
      bestFor: 'Building long-term trust and meeting the highest security requirements of enterprise clients.',
      icon: TrendingUp,
      color: 'green'
    }
  ];

  const benefits = [
    {
      icon: FileCheck,
      title: 'Market Entry',
      description: 'Many large enterprises and government agencies require a SOC 2 Type II report before signing contracts with SaaS or cloud providers.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      icon: Shield,
      title: 'Risk Mitigation',
      description: 'The audit process helps identify internal vulnerabilities before they can be exploited, reducing the likelihood of data breaches.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      icon: Award,
      title: 'Marketing Advantage',
      description: 'A SOC 2 report serves as a "seal of approval," demonstrating to the market that you take data protection seriously.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    }
  ];

  const handleScroll = (href) => {
    if (href.startsWith('#')) {
      navigate('/' + href);
    }
  };

  return (
    <section id="soc2-services" className="pt-12 sm:pt-16 md:pt-20 pb-20 sm:pb-24 md:pb-32 bg-gradient-to-b from-slate-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold text-xs uppercase tracking-wider">
              <Shield className="w-4 h-4" />
              Security & Trust Compliance
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            SOC 2 <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600"> Services</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            Demonstrate security and trust to your customers through comprehensive SOC 2 audits. We guide service organizations through the complete compliance journey for cloud-based businesses.
          </p>
        </div>

        {/* What is SOC 2 Section */}
        <div className="mb-20 sm:mb-24 md:mb-28">
          <div className="bg-gradient-to-br from-green-50 via-white to-emerald-50 rounded-2xl p-8 sm:p-12 shadow-lg border-2 border-green-200/50">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">What is SOC 2?</h3>
                <p className="text-slate-700 text-lg leading-relaxed">
                  A SOC 2 (System and Organization Controls) audit is a voluntary compliance standard developed by the AICPA specifically for service-oriented businesses that handle customer data in the cloud. SOC 2 is a technical audit that verifies whether your internal security controls actually work as intended.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Services Criteria */}
        <div className="mb-20 sm:mb-24 md:mb-28">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">Trust Services Criteria (TSC)</h3>
            <p className="text-slate-600 max-w-3xl mx-auto">
              The audit evaluates your organization based on five specific criteria. While <span className="font-semibold text-green-600">Security is mandatory</span>, you can choose additional categories based on your business model.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trustServicesCriteria.map((criteria, index) => {
              const IconComponent = criteria.icon;
              return (
                <div 
                  key={index} 
                  className={`bg-white rounded-2xl p-8 border-2 ${criteria.isMandatory ? 'border-green-300 shadow-lg' : 'border-slate-200'} hover:shadow-2xl transition-all duration-300 group relative`}
                >
                  {criteria.isMandatory && (
                    <div className="absolute top-3 right-3">
                      <span className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold">
                        MANDATORY
                      </span>
                    </div>
                  )}
                  <div className={`w-16 h-16 bg-gradient-to-br ${criteria.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-green-600 transition-colors">
                    {criteria.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    {criteria.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* SOC 2 Type I vs Type II */}
        <div className="mb-20 sm:mb-24 md:mb-28">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">SOC 2 Type I vs. Type II</h3>
            <p className="text-slate-600 max-w-3xl mx-auto">
              Understanding the difference between these two report types is critical for planning your audit timeline.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {reportTypes.map((report, index) => {
              const IconComponent = report.icon;
              const isTypeTwo = report.type === 'Type II';
              return (
                <div 
                  key={index} 
                  className={`bg-gradient-to-br ${isTypeTwo ? 'from-green-50 to-emerald-50 border-green-300' : 'from-blue-50 to-cyan-50 border-blue-300'} border-2 rounded-2xl p-8 hover:shadow-xl transition-all duration-300`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 ${isTypeTwo ? 'bg-green-600' : 'bg-blue-600'} rounded-xl flex items-center justify-center`}>
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="text-2xl font-bold text-slate-900">{report.type}</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Focus Area:</p>
                      <p className="text-slate-600 leading-relaxed">{report.focus}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Best For:</p>
                      <p className="text-slate-600 leading-relaxed">{report.bestFor}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why Pursue SOC 2 Certification */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">Why Pursue SOC 2 Certification?</h3>
            <p className="text-slate-600 max-w-3xl mx-auto">
              SOC 2 compliance delivers strategic advantages that go beyond security, positioning your organization for growth and trust.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div 
                  key={index} 
                  className={`${benefit.bgColor} border ${benefit.borderColor} rounded-xl p-6 hover:shadow-lg transition-all duration-300`}
                >
                  <div className={`w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                    <IconComponent className={`w-6 h-6 ${benefit.color}`} />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">
                    {benefit.title}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 rounded-3xl p-10 sm:p-16 text-white text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full -ml-20 -mb-20"></div>
          <div className="relative z-10">
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Achieve SOC 2 Compliance?</h3>
            <p className="text-lg text-green-100 mb-8 max-w-2xl mx-auto">
              Our experienced auditors will guide you through every step of the SOC 2 audit process, from scoping to final report delivery.
            </p>
            <Button 
              onClick={() => handleScroll('#contact')} 
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-green-50 text-green-700 font-bold px-8 py-4 rounded-xl hover:shadow-xl transition-all duration-300"
            >
              Start Your SOC 2 Journey
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Soc2Services;
